import { useRef, useCallback, useEffect, useState } from "react";

/**
 * Bubble animation state interface
 */
interface BubbleState {
  width: string;
  transform: string;
  opacity: number;
}

/**
 * Hook props interface
 */
interface UseAnimatedBubbleProps<T> {
  /** Current selected value */
  value: T | T[];
  /** Available options */
  options: Array<{ value: T; label: string; disabled?: boolean }>;
}

/**
 * Custom hook for managing animated bubble indicator in SegmentedControl
 *
 * @template T - Type of option values
 * @param props - Hook configuration
 * @returns Object containing container ref and bubble styles
 *
 * @example
 * ```tsx
 * const { containerRef, bubbleStyle } = useAnimatedBubble({
 *   value: selectedValue,
 *   options: segmentOptions
 * });
 * ```
 */
export const useAnimatedBubble = <T extends string = string>({
  value,
  options,
}: UseAnimatedBubbleProps<T>) => {
  // Refs for DOM elements
  const containerRef = useRef<HTMLDivElement>(null);

  // Bubble animation state
  const [bubbleStyle, setBubbleStyle] = useState<BubbleState>({
    width: "0%",
    transform: "translateX(0px)",
    opacity: 0,
  });

  /**
   * Calculate and update bubble position based on active button
   */
  const updateBubblePosition = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const buttons = container.querySelectorAll(".MuiToggleButton-root");
    const activeIndex = options.findIndex((option) => option.value === value);

    // Exit early if no active button found
    if (activeIndex === -1 || !buttons[activeIndex]) return;

    const activeButton = buttons[activeIndex] as HTMLElement;
    const containerRect = container.getBoundingClientRect();
    const buttonRect = activeButton.getBoundingClientRect();

    // Calculate bubble width using percentage for responsive behavior
    const widthPercentage = 100 / options.length;

    // Calculate position using pixels for accurate positioning
    const translateXPixels = buttonRect.left - containerRect.left;

    setBubbleStyle({
      width: `${widthPercentage}%`,
      transform: `translateX(${translateXPixels}px)`,
      opacity: 1,
    });
  }, [options, value]);

  /**
   * Update bubble position when value changes
   * Uses timeout to ensure DOM is updated before calculation
   */
  useEffect(() => {
    const timeoutId = setTimeout(updateBubblePosition, 0);

    return () => clearTimeout(timeoutId);
  }, [value, updateBubblePosition]);

  /**
   * Update bubble position when window resizes
   * Ensures responsive behavior on screen size changes
   */
  useEffect(() => {
    const handleResize = () => {
      updateBubblePosition();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [updateBubblePosition]);

  return {
    /** Ref for the container element */
    containerRef,
    /** Current bubble styling object */
    bubbleStyle,
    /** Function to manually update bubble position */
    updateBubblePosition,
  };
};
